import { useState, useEffect } from "react";
import { usePrivy, useWallets } from "@privy-io/react-auth";
import {
  Wallet,
  Copy,
  ArrowDownToLine,
  Eye,
  EyeOff,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
} from "lucide-react";
import { API_BASE } from "@/lib/api";

export default function WalletPage() {
  const { login, logout, authenticated, user } = usePrivy();
  const { wallets } = useWallets();
  const [embeddedWallet, setEmbeddedWallet] = useState<any>(null);
  const [balance, setBalance] = useState(0.0);
  const [showExport, setShowExport] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportData, setExportData] = useState<any>(null);
  const [showDeposit, setShowDeposit] = useState(false);
  const [depositState, setDepositState] = useState<any>(null);

  // Find embedded wallet
  useEffect(() => {
    if (wallets && wallets.length > 0) {
      const embedded = wallets.find((w: any) => w.walletClientType === "privy");
      setEmbeddedWallet(embedded || null);
    }
  }, [wallets]);

  // Fetch balance from backend
  useEffect(() => {
    if (authenticated && user?.id) {
      fetch(`${API_BASE}/api/wallet/balance/${user.id}`)
        .then((r) => r.json())
        .then((data) => setBalance(data.balance_pusd || 0))
        .catch(() => setBalance(0));
    }
  }, [authenticated, user]);

  const copyAddress = (addr: string) => {
    navigator.clipboard.writeText(addr);
  };

  const handleExport = async () => {
    if (!user?.id) return;
    setExportLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/wallet/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id }),
      });
      const data = await res.json();
      setExportData(data.data);
    } catch (e) {
      console.error(e);
    }
    setExportLoading(false);
  };

  const handleDeposit = async (fromChain: string, token: string, amount: number) => {
    if (!user?.id) return;
    try {
      const res = await fetch(`${API_BASE}/api/wallet/deposit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, from_chain: fromChain, token, amount }),
      });
      const data = await res.json();
      setDepositState(data);
    } catch (e) {
      console.error(e);
    }
  };

  if (!authenticated) {
    return (
      <div className="p-6 space-y-6 max-w-4xl">
        <div className="flex items-center gap-3">
          <Wallet size={22} className="text-terminal-accent" />
          <div>
            <h1 className="text-xl font-bold text-white">WALLET</h1>
            <p className="text-xs text-gray-500">Connect your wallet to start trading on Polymarket</p>
          </div>
        </div>

        <div className="terminal-window p-8 text-center">
          <Wallet size={48} className="mx-auto text-gray-600 mb-4" />
          <h2 className="text-lg text-white mb-2">Connect Wallet</h2>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            Connect with MetaMask, Coinbase, or any wallet. We'll create an embedded 
            trading wallet for you that works directly with Polymarket.
          </p>
          <button onClick={login} className="terminal-btn-primary inline-flex items-center gap-2">
            <Wallet size={14} />
            CONNECT WALLET
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Wallet size={22} className="text-terminal-accent" />
          <div>
            <h1 className="text-xl font-bold text-white">WALLET</h1>
            <p className="text-xs text-gray-500">Manage your embedded trading wallet</p>
          </div>
        </div>
        <button onClick={logout} className="terminal-btn text-xs">DISCONNECT</button>
      </div>

      {/* Embedded Wallet Card */}
      <div className="terminal-window p-4">
        <div className="text-xs text-terminal-accent mb-2 flex items-center gap-2">
          <CheckCircle2 size={12} />
          EMBEDDED TRADING WALLET (POLYGON)
        </div>
        <div className="flex items-center gap-2 bg-black/50 border border-terminal-border rounded px-3 py-2">
          <code className="text-sm text-terminal-fg flex-1 truncate">
            {embeddedWallet?.address || "Creating wallet..."}
          </code>
          <button
            onClick={() => embeddedWallet?.address && copyAddress(embeddedWallet.address)}
            className="text-gray-500 hover:text-terminal-fg"
          >
            <Copy size={14} />
          </button>
          <a
            href={embeddedWallet?.address ? `https://polygonscan.com/address/${embeddedWallet.address}` : "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 hover:text-terminal-fg"
          >
            <ExternalLink size={14} />
          </a>
        </div>
        <p className="text-[10px] text-gray-500 mt-2">
          This wallet is used for all Polymarket trades. Deposits convert to pUSD automatically.
        </p>
      </div>

      {/* Balance + Actions */}
      <div className="grid grid-cols-2 gap-4">
        <div className="terminal-window p-4">
          <div className="text-xs text-gray-500 mb-1">BALANCE</div>
          <div className="text-2xl font-bold text-white">{balance.toFixed(2)} pUSD</div>
          <div className="text-xs text-gray-500 mt-1">Polymarket Trading Balance</div>
        </div>
        <div className="terminal-window p-4 flex flex-col justify-between">
          <div className="text-xs text-gray-500">ACTIONS</div>
          <button onClick={() => setShowDeposit(true)} className="terminal-btn-primary flex items-center justify-center gap-2 mt-3">
            <ArrowDownToLine size={14} />
            DEPOSIT
          </button>
        </div>
      </div>

      {/* Deposit Modal */}
      {showDeposit && (
        <DepositModal
          onClose={() => setShowDeposit(false)}
          onDeposit={handleDeposit}
          depositState={depositState}
          walletAddress={embeddedWallet?.address}
        />
      )}

      {/* Export Private Key */}
      <div className="terminal-window p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-terminal-danger flex items-center gap-2">
            <AlertTriangle size={12} />
            EXPORT PRIVATE KEY
          </div>
          <button onClick={() => setShowExport(!showExport)} className="text-[10px] text-gray-500 hover:text-white">
            {showExport ? "HIDE" : "SHOW"}
          </button>
        </div>

        {!showExport ? (
          <p className="text-xs text-gray-500">
            Export your embedded wallet's private key to use it in MetaMask or other wallets.
            Never share this with anyone.
          </p>
        ) : (
          <div className="space-y-3">
            {!exportData ? (
              <button onClick={handleExport} disabled={exportLoading} className="terminal-btn-danger text-xs">
                {exportLoading ? (
                  <RefreshCw size={12} className="animate-spin inline mr-1" />
                ) : (
                  <Eye size={12} className="inline mr-1" />
                )}
                {exportLoading ? "ENCRYPTING..." : "EXPORT PRIVATE KEY"}
              </button>
            ) : (
              <div className="bg-black/50 border border-terminal-danger rounded p-3">
                <p className="text-[10px] text-terminal-danger mb-2">
                  WARNING: Store this securely. Anyone with this key controls your funds.
                </p>
                <pre className="text-xs text-yellow-400 font-mono break-all whitespace-pre-wrap">
                  {JSON.stringify(exportData, null, 2)}
                </pre>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(exportData));
                  }}
                  className="mt-2 text-[10px] text-terminal-accent hover:underline"
                >
                  Copy to clipboard
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DepositModal({
  onClose,
  onDeposit,
  depositState,
  walletAddress,
}: {
  onClose: () => void;
  onDeposit: (chain: string, token: string, amount: number) => void;
  depositState: any;
  walletAddress: string;
}) {
  const [chain, setChain] = useState("ethereum");
  const [token, setToken] = useState("USDC");
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState(0);

  const chains = [
    { id: "ethereum", name: "Ethereum", icon: "◈" },
    { id: "base", name: "Base", icon: "◉" },
    { id: "polygon", name: "Polygon", icon: "◎" },
  ];

  const tokens = ["USDC", "USDT"];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="terminal-window w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="terminal-header flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ArrowDownToLine size={12} />
            DEPOSIT TO POLYMARKET
          </span>
          <button onClick={onClose} className="text-gray-500 hover:text-white">✕</button>
        </div>

        <div className="p-4 space-y-4">
          {!depositState ? (
            <>
              {/* Step 1: Select Chain */}
              <div>
                <label className="text-xs text-gray-500 block mb-2">FROM CHAIN</label>
                <div className="grid grid-cols-3 gap-2">
                  {chains.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setChain(c.id)}
                      className={`p-2 border rounded text-xs text-center cursor-pointer ${
                        chain === c.id
                          ? "border-terminal-accent text-terminal-accent bg-terminal-accent/10"
                          : "border-terminal-border text-gray-400 hover:text-white"
                      }`}
                    >
                      <div className="text-lg mb-1">{c.icon}</div>
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 2: Select Token */}
              <div>
                <label className="text-xs text-gray-500 block mb-2">TOKEN</label>
                <div className="flex gap-2">
                  {tokens.map((t) => (
                    <button
                      key={t}
                      onClick={() => setToken(t)}
                      className={`px-3 py-2 border rounded text-xs cursor-pointer ${
                        token === t
                          ? "border-terminal-accent text-terminal-accent bg-terminal-accent/10"
                          : "border-terminal-border text-gray-400 hover:text-white"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3: Amount */}
              <div>
                <label className="text-xs text-gray-500 block mb-2">AMOUNT</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="terminal-input flex-1"
                  />
                  <span className="text-xs text-gray-500">{token}</span>
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  Min deposit: 5 {token}. Funds convert to pUSD automatically.
                </p>
              </div>

              {/* Destination */}
              <div className="bg-black/30 border border-terminal-border rounded p-3">
                <div className="text-[10px] text-gray-500 mb-1">DESTINATION</div>
                <div className="text-xs text-white font-mono truncate">{walletAddress}</div>
                <div className="text-[10px] text-terminal-accent mt-1">Polymarket Embedded Wallet</div>
              </div>

              {/* Submit */}
              <button
                onClick={() => onDeposit(chain, token, parseFloat(amount))}
                disabled={!amount || parseFloat(amount) <= 0}
                className="terminal-btn-primary w-full flex items-center justify-center gap-2"
              >
                <ArrowDownToLine size={14} />
                INITIATE DEPOSIT
              </button>
            </>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <CheckCircle2 size={32} className="mx-auto text-terminal-accent mb-2" />
                <h3 className="text-white font-bold">Deposit Initiated</h3>
              </div>

              <div className="bg-black/30 border border-terminal-border rounded p-3 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Deposit ID</span>
                  <span className="text-white font-mono">#{depositState.deposit_id}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">From</span>
                  <span className="text-white">{depositState.from_chain} → polygon</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Amount</span>
                  <span className="text-white">{depositState.amount} {depositState.token}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Destination</span>
                  <span className="text-terminal-accent font-mono">{depositState.destination_address?.slice(0, 12)}...</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs text-gray-500">STEPS:</div>
                {depositState.steps?.map((s: string, i: number) => (
                  <div key={i} className="text-xs text-gray-300 flex items-start gap-2">
                    <span className="text-terminal-accent shrink-0">{i + 1}.</span>
                    {s}
                  </div>
                ))}
              </div>

              <div className="text-[10px] text-gray-500 text-center">
                Estimated time: {depositState.estimated_time}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
